/**
 * The "?" help button, and the conversation behind it.
 *
 * 🔴 WHAT THIS REPLACES. The same button was on every page for months and its
 * submit handler made NO REQUEST OF ANY KIND — it toasted "Support request
 * submitted. We will get back to you within 24 hours", showed "We will respond
 * to {your email} within 24 hours", and cleared the message from React state
 * two seconds later. Verified on the deployed build: typing a message and
 * pressing Send produced zero calls on the wire. It was unmounted rather than
 * patched, because the blocker was the RECIPIENT and not the handler: there
 * was no route, no inbox and no address anywhere in the codebase.
 *
 * There is a recipient now. The message is stored in the product and read by a
 * super admin, and the reply comes back here.
 *
 * 🔴 BUG_84 — EVERY SIGNED-IN USER, AND THREE FRONT DOORS. The button used to
 * return null for a super admin ("they read the inbox, a button would be a
 * loop"). The tester signed in as one, found no button anywhere and an empty
 * inbox, and reported the whole feature missing — production held zero
 * threads. A super admin is also somebody who finds bugs and wants features,
 * so the button shows for every signed-in user, and a super admin gets a link
 * to the inbox beside it. It opens on three choices — Report a bug, Suggest a
 * feature, Ask a question — then a form with a subject, details and an
 * optional attachment, then a plain success message.
 *
 * 🔴 THE COPY PROMISES A MECHANISM, NEVER A TIME. "The Spaciora team will see
 * this" is the whole promise — there is no "within 24 hours" anywhere in this
 * file, and there must not be, because there is no time we can keep. Nothing
 * says "emailed" either: SendGrid is out of credit and the product IS the
 * delivery mechanism. The thread is the record; the dot on this button is the
 * marker.
 *
 * 🔴 THE MESSAGE GOES FIRST, THE FILE SECOND. The attachment is uploaded after
 * the thread exists, through the same storage every other upload uses. A
 * storage outage (402s for days in September) therefore costs the file, not
 * the message — and the success screen says which of the two arrived.
 *
 * THE PAGE IS DISCLOSED, NOT COLLECTED SILENTLY. Whatever page you were on
 * travels with the message, and the form says so above the Send button before
 * you press it.
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  QuestionMarkCircleIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  ArrowLeftIcon,
  BugAntIcon,
  LightBulbIcon,
  ChatBubbleLeftEllipsisIcon,
  PaperClipIcon,
  CheckCircleIcon,
  InboxIcon,
} from '@heroicons/react/24/outline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';

import { apiGet, apiPost, apiUpload } from '@/utils/api';
import { useAuthStore } from '@/stores/authStore';
import { display } from '@/utils/dateDisplay';
import { routeRequestError } from '@/utils/validation';
import toast from 'react-hot-toast';

//: A label on ONE inbox — it routes nowhere, and the copy must not imply it
//: does, because there is no billing team to route to. "feature" arrived with
//: the three front doors (Bug_84); the other four were always here.
export const CATEGORIES = [
  ['bug', 'Something is broken'],
  ['feature', 'A feature I would like'],
  ['general', 'General question'],
  ['billing', 'Billing'],
  ['account', 'My account'],
] as const;

//: The three doors the widget opens on. Each one only pre-selects a category;
//: the form still lets the sender change it.
export const CHOICES = [
  {
    key: 'bug', label: 'Report a bug', icon: BugAntIcon,
    hint: 'Something is broken or not behaving as it should.',
    subjectPlaceholder: 'e.g. The export button does nothing',
    detailsPlaceholder: 'What happened, and what did you expect to happen?',
  },
  {
    key: 'feature', label: 'Suggest a feature', icon: LightBulbIcon,
    hint: 'Something you wish the product did.',
    subjectPlaceholder: 'e.g. Export bookings to Excel',
    detailsPlaceholder: 'What would you like it to do, and what would it help you with?',
  },
  {
    key: 'general', label: 'Ask a question', icon: ChatBubbleLeftEllipsisIcon,
    hint: 'How do I…? Where is…? Anything else.',
    subjectPlaceholder: 'e.g. How do I add a second building?',
    detailsPlaceholder: 'What would you like to know?',
  },
] as const;

//: What the server will store (utils/uploads.py ATTACHMENT_TYPES) and its
//: 10 MB bucket limit. Checked here only so the sender hears it before
//: pressing Send — the server decides from the bytes either way.
const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,.pdf,.docx,.xlsx,.pptx';
const MAX_BYTES = 10 * 1024 * 1024;

interface ThreadMessage {
  id: string;
  side: 'customer' | 'spaciora';
  author_name: string;
  body: string;
  created_at: string;
}

interface ThreadAttachment {
  id: string;
  file_name: string;
  url: string | null;
  created_at: string;
}

interface Thread {
  id: string;
  subject: string | null;
  category: string;
  status: string;
  visibility: string;
  page_path: string | null;
  created_at: string;
  last_message_at: string;
  unread: boolean;
  who_can_see: string;
  messages?: ThreadMessage[];
  attachments?: ThreadAttachment[] | null;
}

const STATUS_WORDS: Record<string, string> = {
  new: 'Waiting for Spaciora',
  read: 'Spaciora has read it',
  answered: 'Replied',
  closed: 'Closed',
};

type View = 'home' | 'form' | 'sent' | 'thread';

interface SentResult {
  threadId: string;
  attachment: 'none' | 'stored' | 'failed';
  attachmentError?: string;
}

/** The event the top-bar help button sends: the dialog's state lives here. */
const OPEN_SUPPORT_EVENT = 'spaciora:open-support';

function useSupportUnread() {
  const me = useAuthStore((s) => s.user);
  // 🔴 THE DOT IS THE SUPPORT SURFACE'S OWN, NOT THE NOTIFICATION BELL. 370 of
  // the 374 notifications on the deployment are unread; a support reply
  // dropped in there is invisible on arrival. The bell gets one too, as a
  // second surface, never as the only one. One query key, so the top-bar
  // button and the floating one share one request.
  const unreadQ = useQuery<{ unread: number }>({
    queryKey: ['support', 'unread'],
    queryFn: () => apiGet('/support/threads/unread-count'),
    enabled: Boolean(me),
    refetchInterval: 120_000,
  });
  return unreadQ.data?.unread ?? 0;
}

function UnreadDot({ unread }: { unread: number }) {
  return unread > 0 ? (
    <span
      data-testid="support-unread-dot"
      aria-label={`${unread} unread ${unread === 1 ? 'reply' : 'replies'}`}
      className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-danger-600 text-[10px] font-bold text-content-inverse"
    >
      {unread}
    </span>
  ) : null;
}

/**
 * The "?" in the TOP BAR, below lg (Employee fix, final round).
 *
 * 🔴 A FLOATING BUTTON COVERS WHAT IS UNDER IT. At 360 px the bottom-right
 * "?" sat on Settings → Notifications' "Email for everything" switch on first
 * view — and right-aligned switches, toggles and "Next" buttons are where a
 * phone page puts them. Padding at the foot of a page only helps the LAST
 * row. Below lg the help button is in the top bar, beside the bell, where it
 * covers nothing; from lg the floating one stays (a wide page has room).
 */
export function SupportHeaderButton() {
  const unread = useSupportUnread();
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new Event(OPEN_SUPPORT_EVENT))}
      data-testid="support-button-header"
      className="relative rounded-lg p-1.5 text-content-tertiary hover:bg-surface-muted lg:hidden"
      title="Help and support"
      aria-label="Help and support"
      data-no-print
    >
      <QuestionMarkCircleIcon className="h-6 w-6" />
      <UnreadDot unread={unread} />
    </button>
  );
}

export default function SupportModal() {
  const me = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<View>('home');
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [category, setCategory] = useState<string>('general');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [privateOnly, setPrivateOnly] = useState(false);
  const [sent, setSent] = useState<SentResult | null>(null);
  const [reply, setReply] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  const unread = useSupportUnread();
  useEffect(() => {
    const openIt = () => setIsOpen(true);
    window.addEventListener(OPEN_SUPPORT_EVENT, openIt);
    return () => window.removeEventListener(OPEN_SUPPORT_EVENT, openIt);
  }, []);

  const threadsQ = useQuery<{ data: Thread[]; unread: number }>({
    queryKey: ['support', 'threads'],
    queryFn: () => apiGet('/support/threads?limit=50'),
    enabled: isOpen,
  });
  const threads = threadsQ.data?.data ?? [];

  const threadQ = useQuery<Thread>({
    queryKey: ['support', 'thread', openThreadId],
    queryFn: () => apiGet(`/support/threads/${openThreadId}`),
    enabled: isOpen && view === 'thread' && Boolean(openThreadId),
  });

  // Reading a thread clears the dot on the server, so both counts have to be
  // asked again rather than adjusted here — two surfaces guessing separately
  // is how they come to disagree.
  useEffect(() => {
    if (threadQ.data) {
      queryClient.invalidateQueries({ queryKey: ['support', 'unread'] });
      queryClient.invalidateQueries({ queryKey: ['support', 'threads'] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadQ.data?.id]);

  const resetForm = () => {
    setSubject('');
    setMessage('');
    setFile(null);
    setFileError(null);
    setPrivateOnly(false);
    if (fileInput.current) fileInput.current.value = '';
  };

  const send = useMutation({
    mutationFn: async (): Promise<SentResult> => {
      const thread = await apiPost<Thread>('/support/threads', {
        message: message.trim(),
        subject: subject.trim() || null,
        category,
        private: privateOnly,
        // Where they were. Shown on the form before this is sent.
        page_path: `${location.pathname}${location.search}`,
      });
      if (!file) return { threadId: thread.id, attachment: 'none' };
      // The message is stored. A failed upload from here on costs the file
      // and nothing else, and the success screen says so.
      try {
        const fd = new FormData();
        fd.append('file', file);
        await apiUpload(`/support/threads/${thread.id}/attachments`, fd);
        return { threadId: thread.id, attachment: 'stored' };
      } catch (e) {
        return {
          threadId: thread.id,
          attachment: 'failed',
          attachmentError: routeRequestError(e, {})?.message ?? 'The file could not be stored.',
        };
      }
    },
    onSuccess: (result) => {
      setSent(result);
      setView('sent');
      resetForm();
      toast.success('Message submitted successfully');
      queryClient.invalidateQueries({ queryKey: ['support'] });
    },
    onError: (e) => toast.error(
      routeRequestError(e, {})?.message ?? 'Could not send your message'),
  });

  const addReply = useMutation({
    mutationFn: () => apiPost(`/support/threads/${openThreadId}/messages`, {
      message: reply.trim(),
    }),
    onSuccess: () => {
      setReply('');
      queryClient.invalidateQueries({ queryKey: ['support'] });
    },
    onError: (e) => toast.error(
      routeRequestError(e, {})?.message ?? 'Could not send your message'),
  });

  // Every signed-in user, super admins included (Bug_84). Only a signed-out
  // visitor gets nothing — and the layout this is mounted in is signed-in
  // only anyway.
  if (!me) return null;
  const isOperator = me.role === 'super_admin';

  const close = () => {
    setIsOpen(false);
    setView('home');
    setOpenThreadId(null);
    setSent(null);
  };
  const choose = (key: string) => {
    setCategory(key);
    setView('form');
  };
  const openThread = (id: string) => {
    setOpenThreadId(id);
    setView('thread');
  };
  const pickFile = (f: File | null) => {
    setFileError(null);
    if (f && f.size > MAX_BYTES) {
      setFile(null);
      setFileError('That file is larger than 10 MB.');
      if (fileInput.current) fileInput.current.value = '';
      return;
    }
    setFile(f);
  };

  const choice = CHOICES.find((c) => c.key === category);
  const current = threadQ.data;
  const canSend = Boolean(subject.trim() && message.trim()) && !send.isPending;

  const title = view === 'thread' ? 'Your message'
    : view === 'form' ? (choice?.label ?? 'Send a message')
    : view === 'sent' ? 'Message submitted'
    : 'Help and support';

  return (
    <>
      {/* 🔴 BOTTOM-RIGHT FROM lg ONLY. Toasts are top-right (main.tsx), and
          main reserves bottom padding so the last row of a page can scroll
          out from under it. Below lg it is SupportHeaderButton in the top
          bar instead: floating, it covered a phone page's right-hand
          switches (Employee fix, final round). z-40 sits under every dialog
          (z-50) and the onboarding tour. */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        data-testid="support-button"
        className="fixed bottom-6 right-4 z-40 hidden h-12 w-12 items-center justify-center rounded-full bg-brand-700 text-content-inverse shadow-lg transition-transform hover:scale-110 lg:flex"
        title="Help and support"
        aria-label="Help and support"
        data-no-print
      >
        <QuestionMarkCircleIcon className="h-7 w-7" />
        <UnreadDot unread={unread} />
      </button>

      <Transition show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={close}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-scrim/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-end p-4 sm:items-center sm:justify-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-surface-raised p-6 shadow-xl">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {(view === 'form' || view === 'thread') && (
                        <button type="button" onClick={() => { setView('home'); setOpenThreadId(null); }}
                                data-testid="support-back" aria-label="Back"
                                className="rounded p-1 hover:bg-surface-subtle">
                          <ArrowLeftIcon className="h-4 w-4 text-content-muted" />
                        </button>
                      )}
                      <Dialog.Title className="text-base font-semibold text-content">
                        {title}
                      </Dialog.Title>
                    </div>
                    <button type="button" onClick={close} aria-label="Close"
                            className="rounded p-1 hover:bg-surface-subtle">
                      <XMarkIcon className="h-5 w-5 text-content-muted" />
                    </button>
                  </div>

                  {view === 'thread' && (
                    <ThreadView
                      thread={current}
                      loading={threadQ.isLoading}
                      reply={reply}
                      setReply={setReply}
                      onSend={() => addReply.mutate()}
                      sending={addReply.isPending}
                    />
                  )}

                  {view === 'sent' && sent && (
                    <div className="mt-4 space-y-3" data-testid="support-sent">
                      <p className="flex items-center gap-2 text-sm font-medium text-success-700">
                        <CheckCircleIcon className="h-5 w-5" />
                        Message submitted successfully.
                      </p>
                      <p className="text-sm text-content-secondary" data-testid="support-promise">
                        The Spaciora team will see this. The reply appears here,
                        under &ldquo;Your messages&rdquo;.
                      </p>
                      {sent.attachment === 'stored' && (
                        <p className="text-xs text-content-muted" data-testid="support-sent-file">
                          Your attachment was stored with it.
                        </p>
                      )}
                      {sent.attachment === 'failed' && (
                        <p className="rounded-lg border border-warning-300 bg-warning-50 p-3 text-xs text-warning-800"
                           data-testid="support-sent-file-failed">
                          Your message was sent, but the attachment was not:{' '}
                          {sent.attachmentError} You can describe it in a reply instead.
                        </p>
                      )}
                      <div className="flex justify-end gap-2">
                        <button type="button" className="btn-secondary"
                                data-testid="support-view-sent"
                                onClick={() => openThread(sent.threadId)}>
                          View your message
                        </button>
                        <button type="button" className="btn-primary" onClick={close}>
                          Done
                        </button>
                      </div>
                    </div>
                  )}

                  {view === 'home' && (
                    <>
                      <p className="mt-1 text-sm text-content-muted">
                        What would you like to do?
                      </p>
                      <div className="mt-4 grid gap-2">
                        {CHOICES.map((c) => {
                          const Icon = c.icon;
                          return (
                            <button
                              key={c.key} type="button"
                              data-testid={`support-choice-${c.key}`}
                              onClick={() => choose(c.key)}
                              className="flex items-start gap-3 rounded-lg border border-line p-3 text-left hover:border-brand-300 hover:bg-surface-subtle"
                            >
                              <Icon className="mt-0.5 h-5 w-5 shrink-0 text-brand-700" />
                              <span>
                                <span className="block text-sm font-medium text-content">{c.label}</span>
                                <span className="block text-xs text-content-muted">{c.hint}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>

                      {isOperator && (
                        <Link
                          to="/super-admin/support" onClick={close}
                          data-testid="support-open-inbox"
                          className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-brand-700 hover:underline"
                        >
                          <InboxIcon className="h-4 w-4" />
                          Open the support inbox
                        </Link>
                      )}

                      <div className="mt-5 border-t border-line pt-4">
                        <h4 className="text-xs font-semibold uppercase tracking-wide text-content-muted">
                          Your messages
                        </h4>
                        {threadsQ.isLoading ? (
                          <p className="mt-2 text-sm text-content-muted">Loading…</p>
                        ) : threads.length === 0 ? (
                          <p className="mt-2 text-sm text-content-muted" data-testid="support-no-threads">
                            Nothing yet. Anything you send appears here with the reply.
                          </p>
                        ) : (
                          <ul className="mt-2 max-h-56 divide-y divide-line-subtle overflow-y-auto">
                            {threads.map((t) => (
                              <li key={t.id}>
                                <button
                                  type="button" data-testid="support-thread-row"
                                  onClick={() => openThread(t.id)}
                                  className="flex w-full items-center justify-between gap-3 py-2 text-left hover:bg-surface-subtle"
                                >
                                  <span className="min-w-0">
                                    <span className="block truncate text-sm text-content">
                                      {t.subject || CATEGORIES.find(([v]) => v === t.category)?.[1]
                                        || t.category}
                                    </span>
                                    <span className="block text-xs text-content-muted">
                                      {STATUS_WORDS[t.status] ?? t.status}
                                      {' · '}
                                      {display(t.last_message_at, 'dateLong')}
                                      {t.visibility === 'private' && ' · Only Spaciora'}
                                    </span>
                                  </span>
                                  {t.unread && (
                                    <span data-testid="support-thread-unread"
                                          className="shrink-0 rounded-full bg-danger-600 px-2 py-0.5 text-[10px] font-bold text-content-inverse">
                                      New reply
                                    </span>
                                  )}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </>
                  )}

                  {view === 'form' && (
                    <form
                      className="mt-4 space-y-3"
                      onSubmit={(e) => { e.preventDefault(); if (canSend) send.mutate(); }}
                    >
                      <div>
                        <label htmlFor="support-category"
                               className="mb-1 block text-xs font-medium text-content-secondary">
                          Category
                        </label>
                        <select id="support-category" className="input-field"
                                data-testid="support-category"
                                value={category} onChange={(e) => setCategory(e.target.value)}>
                          {CATEGORIES.map(([v, label]) => (
                            <option key={v} value={v}>{label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="support-subject"
                               className="mb-1 block text-xs font-medium text-content-secondary">
                          Subject
                        </label>
                        <input
                          id="support-subject" className="input-field" maxLength={200}
                          data-testid="support-subject"
                          placeholder={choice?.subjectPlaceholder ?? 'A short summary'}
                          value={subject} onChange={(e) => setSubject(e.target.value)}
                        />
                      </div>

                      <div>
                        <label htmlFor="support-message"
                               className="mb-1 block text-xs font-medium text-content-secondary">
                          Details
                        </label>
                        <textarea
                          id="support-message" rows={4} className="input-field" maxLength={5000}
                          data-testid="support-message"
                          placeholder={choice?.detailsPlaceholder ?? 'Tell us more'}
                          value={message} onChange={(e) => setMessage(e.target.value)}
                        />
                      </div>

                      <div>
                        <label htmlFor="support-file"
                               className="mb-1 flex items-center gap-1 text-xs font-medium text-content-secondary">
                          <PaperClipIcon className="h-3.5 w-3.5" />
                          Attachment <span className="font-normal text-content-subtle">(optional — a screenshot, PDF or Office file, up to 10 MB)</span>
                        </label>
                        <input
                          id="support-file" ref={fileInput} type="file" accept={ACCEPT}
                          data-testid="support-file"
                          className="block w-full text-xs text-content-secondary file:mr-3 file:rounded file:border-0 file:bg-surface-subtle file:px-3 file:py-1.5 file:text-xs file:font-medium"
                          onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
                        />
                        {fileError && (
                          <p className="mt-1 text-xs text-danger-600" data-testid="support-file-error">
                            {fileError}
                          </p>
                        )}
                      </div>

                      {/* 🔴 THE SENDER CHOOSES, AND THE LABEL SAYS WHAT IT
                          DOES. By default an org admin can see what their
                          own people raised, which is right — an admin
                          supporting their users should not be blind to it.
                          But a message can be ABOUT that admin, so this box
                          exists and its wording is the whole feature. */}
                      <label className="flex items-start gap-2 rounded-lg border border-line p-3 text-xs">
                        <input
                          type="checkbox" className="mt-0.5 h-3.5 w-3.5"
                          data-testid="support-private"
                          checked={privateOnly}
                          onChange={(e) => setPrivateOnly(e.target.checked)}
                        />
                        <span>
                          <span className="font-medium text-content">Only Spaciora sees this</span>
                          <span className="block text-content-muted">
                            Your organisation&rsquo;s admins won&rsquo;t see this
                            message. Leave it off and they can, which helps
                            them fix things themselves.
                          </span>
                        </span>
                      </label>

                      {/* Disclosed, not silent. */}
                      <p className="text-xs text-content-subtle" data-testid="support-page-note">
                        Sent from <span className="font-mono">{location.pathname}</span>,
                        so we know where you were.
                      </p>

                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs text-content-muted">
                          The Spaciora team will see this. The reply appears here.
                        </p>
                        <button
                          type="submit" className="btn-primary inline-flex items-center gap-1.5"
                          data-testid="support-send"
                          disabled={!canSend}
                        >
                          <PaperAirplaneIcon className="h-4 w-4" />
                          {send.isPending ? 'Sending…' : 'Send'}
                        </button>
                      </div>
                    </form>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}

function ThreadView({
  thread, loading, reply, setReply, onSend, sending,
}: {
  thread: Thread | undefined;
  loading: boolean;
  reply: string;
  setReply: (v: string) => void;
  onSend: () => void;
  sending: boolean;
}) {
  if (loading || !thread) {
    return <p className="mt-4 text-sm text-content-muted">Loading…</p>;
  }
  const closed = thread.status === 'closed';
  const files = thread.attachments ?? [];
  return (
    <div className="mt-4 space-y-3">
      {thread.subject && (
        <p className="text-sm font-medium text-content" data-testid="support-thread-subject">
          {thread.subject}
        </p>
      )}
      <p className="text-xs text-content-muted" data-testid="support-who-can-see">
        {STATUS_WORDS[thread.status] ?? thread.status} · {thread.who_can_see}
      </p>

      <ul className="max-h-64 space-y-3 overflow-y-auto" data-testid="support-thread-messages">
        {(thread.messages ?? []).map((m) => (
          <li key={m.id}
              className={`rounded-lg p-3 text-sm ${
                m.side === 'spaciora' ? 'bg-brand-50' : 'bg-surface-subtle'}`}>
            <p className="text-xs font-medium text-content-secondary"
               data-testid={m.side === 'spaciora' ? 'support-reply-byline' : undefined}>
              {m.author_name} · {display(m.created_at, 'dateTime')}
            </p>
            <p className="mt-1 whitespace-pre-wrap text-content">{m.body}</p>
          </li>
        ))}
      </ul>

      {files.length > 0 && (
        <ul className="space-y-1" data-testid="support-thread-attachments">
          {files.map((a) => (
            <li key={a.id} className="flex items-center gap-1.5 text-xs">
              <PaperClipIcon className="h-3.5 w-3.5 text-content-muted" />
              {a.url ? (
                <a href={a.url} target="_blank" rel="noopener noreferrer"
                   className="text-brand-700 hover:underline">{a.file_name}</a>
              ) : (
                <span className="text-content-muted">{a.file_name} (link unavailable right now)</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {closed ? (
        <p className="rounded-lg border border-line bg-surface-subtle p-3 text-xs text-content-muted"
           data-testid="support-thread-closed">
          This conversation is closed. Send a new message if you need anything else.
        </p>
      ) : (
        <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); if (reply.trim()) onSend(); }}>
          <textarea
            rows={3} className="input-field" data-testid="support-thread-reply"
            placeholder="Add to this conversation" value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
          <div className="flex justify-end">
            <button type="submit" className="btn-primary" data-testid="support-thread-send"
                    disabled={!reply.trim() || sending}>
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
